"use client";
import { CountryListItem } from "@/types/user.types";
import { useEffect, useRef, useState } from "react";
import { ChevronDownSmallIcon } from "@/components/Icons";
import { useClientTranslations } from "@/hooks/useClientTranslations";

export const CountryCodePicker = ({
  countries,
  selectedCountry,
  onSelect,
}: {
  countries: CountryListItem[];
  selectedCountry: CountryListItem;
  onSelect: (country: CountryListItem) => void;
}) => {
  const { t } = useClientTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredCountries = countries.filter(
    (c) =>
      c.countryName.toLowerCase().includes(search.toLowerCase()) ||
      c.countryCode.includes(search),
  );

  const handleSelect = (country: CountryListItem) => {
    onSelect(country);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div ref={dropdownRef} className="relative z-[5]">
      <button
        type="button"
        className="flex items-center gap-1 whitespace-nowrap text-sm font-bold text-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCountry.countryFlag && (
          <img
            src={selectedCountry.countryFlag}
            alt={selectedCountry.countryName}
            className="h-4 w-4 shrink-0 object-cover"
          />
        )}
        <span>{selectedCountry.countryCode}</span>
        <ChevronDownSmallIcon
          className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 z-[5] mb-2 w-[16rem] overflow-hidden rounded-lg border border-white/20 bg-stg-screen-background shadow-lg">
          <div className="sticky top-0 border-b border-white/10 bg-stg-screen-background p-2">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t("auth_screen.search_country")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              className="w-full rounded-md bg-white/10 px-3 py-2 text-sm text-white placeholder-stg-grey-4 outline-none"
            />
          </div>
          <div className="max-h-[12rem] overflow-y-auto">
            {filteredCountries.map((country) => (
              <button
                key={country.country}
                type="button"
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/10 ${
                  country.country === selectedCountry.country
                    ? "bg-white/5"
                    : ""
                }`}
                onClick={() => handleSelect(country)}
              >
                {country.countryFlag && (
                  <img
                    src={country.countryFlag}
                    alt={country.countryName}
                    className="h-4 w-6 object-cover"
                  />
                )}
                <span className="flex-1 truncate text-white">
                  {country.countryName}
                </span>
                <span className="text-stg-grey-4">{country.countryCode}</span>
              </button>
            ))}
            {filteredCountries.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-stg-grey-4">
                {t("auth_screen.no_results_found")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
